'use client';

import { useState, useRef, useCallback, useMemo } from 'react';
import { TweetProps } from '@edusense/social-types';
import { ReferenceTweetType } from '@edusense/social-types';
import { TweetResponse, TweetResponseBasicInfo, BaseUserInfo } from '@edusense/edusense-content-client';
import { Media } from '@edusense/social-types';


/**
 * useTweetStore 훅은 이전 버전이지만 호환성을 위해 제공됨
 * 트윗 작성에 필요한 기본 기능들을 제공합니다.
 */
export const useTweetStore = (selector: any) => {
    // 상태 관리
    const [text, setText] = useState<string>('');
    const [media, setMedia] = useState<Media[]>([]);
    const [pollOptions, setPollOptions] = useState<string[]>(['', '']);
    const [pollDuration, setPollDuration] = useState<number>(24);
    const [isPollActive, setIsPollActive] = useState<boolean>(false);
    const [detectedUrl, setDetectedUrl] = useState<string | null>(null);
    const [linkPreviewData, setLinkPreviewData] = useState<any>(null);
    const [projectPreviews, setProjectPreviews] = useState<any[]>([]);
    const [fileInputRef, setFileInputRef] = useState<React.RefObject<HTMLInputElement> | null>(null);
    const [referenceTweet, setReferenceTweet] = useState<TweetProps | ReferenceTweetType | null>(null);

    // 상태 초기화 함수 - useCallback으로 메모이제이션
    const resetState = useCallback(() => {
        setText('');
        setMedia([]);
        setPollOptions(['', '']);
        setPollDuration(24);
        setIsPollActive(false);
        setDetectedUrl(null);
        setLinkPreviewData(null);
        setProjectPreviews([]);
    }, []);

    // 투표 데이터 생성 함수 - useCallback으로 메모이제이션
    const createPollData = useCallback(() => {
        if (!isPollActive) return null;
        return {
            options: pollOptions.filter(option => option.trim().length > 0),
            durationHours: pollDuration
        };
    }, [isPollActive, pollOptions, pollDuration]);

    // 제출 데이터 생성 함수 - useCallback으로 메모이제이션
    const createSubmitData = useCallback(() => {
        return {
            content: text,
            media,
            poll: createPollData()
        };
    }, [text, media, createPollData]);

    // 허용되는 MIME 타입 목록
    const ALLOWED_MIME_TYPES = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'video/mp4',
        'video/quicktime',
        'video/webm',
        'video/ogg',
        'video/x-matroska' // MKV 추가
    ];

    // 미디어 업로드 핸들러 - useCallback으로 메모이제이션
    const handleMediaUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);
            const validFiles = newFiles.filter(file => {
                // 파일 타입 검증
                if (!ALLOWED_MIME_TYPES.includes(file.type)) {
                    console.warn(`지원하지 않는 파일 형식입니다: ${file.name} (${file.type})`);
                    // 사용자에게 알림 (예: toast 메시지)
                    // import { toast } from 'react-hot-toast'; // 상단에 추가 필요
                    // toast.error(`지원하지 않는 파일 형식입니다: ${file.name}`);
                    return false;
                }
                // 파일 크기 검증 (예: 50MB 제한)
                // const maxSize = 50 * 1024 * 1024;
                // if (file.size > maxSize) {
                //     console.warn(`파일 크기가 너무 큽니다: ${file.name} (${file.size} bytes)`);
                //     toast.error(`파일 크기가 너무 큽니다 (최대 50MB): ${file.name}`);
                //     return false;
                // }
                return true;
            });

            const newMedia = validFiles.map(file => ({
                file,
                url: URL.createObjectURL(file),
                type: file.type.startsWith('image/') ? 'image' : 'video'
            } as Media));

            // 현재 미디어 개수와 새로 추가될 미디어 개수 합쳐서 제한 확인 (예: 최대 4개)
            setMedia(prevMedia => {
                const currentCount = prevMedia.length;
                const availableSlots = 4 - currentCount;
                if (newMedia.length > availableSlots) {
                    console.warn(`미디어 파일은 최대 4개까지 첨부할 수 있습니다.`);
                    // toast.error(`미디어 파일은 최대 4개까지 첨부할 수 있습니다.`);
                    // 앞에서부터 슬롯만큼만 추가
                    return [...prevMedia, ...newMedia.slice(0, availableSlots)];
                }
                return [...prevMedia, ...newMedia];
            });
        }
    }, [ALLOWED_MIME_TYPES]); // 의존성 배열에 ALLOWED_MIME_TYPES 추가

    // 파일 선택 다이얼로그 열기 - useCallback으로 메모이제이션
    const openFileSelector = useCallback(() => {
        fileInputRef?.current?.click();
    }, [fileInputRef]);

    // 상태 객체를 useMemo로 메모이제이션
    const state = useMemo(() => ({
        referenceTweet,
        setText,
        setMedia,
        setPollOptions,
        setPollDuration,
        setIsPollActive,
        setDetectedUrl,
        setLinkPreviewData,
        setProjectPreviews,
        resetState,
        createPollData,
        createSubmitData,
        setFileInputRef,
        handleMediaUpload,
        openFileSelector,
        media,
        text,
    }), [
        referenceTweet,
        setText,
        setMedia,
        setPollOptions,
        setPollDuration,
        setIsPollActive,
        setDetectedUrl,
        setLinkPreviewData,
        setProjectPreviews,
        resetState,
        createPollData,
        createSubmitData,
        setFileInputRef,
        handleMediaUpload,
        openFileSelector,
        media,
        text
    ]);

    // 선택 함수 사용 시 선택한 상태 반환, 아니면 전체 상태 객체 반환
    if (selector && typeof selector === 'function') {
        return selector(state);
    }

    // 기본적으로 필요한 함수와 상태 반환
    return useMemo(() => ({
        text,
        setText,
        media,
        setMedia,
        pollOptions,
        setPollOptions,
        pollDuration,
        setPollDuration,
        isPollActive,
        setIsPollActive,
        detectedUrl,
        setDetectedUrl,
        linkPreviewData,
        setLinkPreviewData,
        projectPreviews,
        setProjectPreviews,
        resetState,
        createPollData,
        createSubmitData,
        setFileInputRef,
        handleMediaUpload,
        openFileSelector,
        referenceTweet,
        setReferenceTweet
    }), [
        text,
        setText,
        media,
        setMedia,
        pollOptions,
        setPollOptions,
        pollDuration,
        setPollDuration,
        isPollActive,
        setIsPollActive,
        detectedUrl,
        setDetectedUrl,
        linkPreviewData,
        setLinkPreviewData,
        projectPreviews,
        setProjectPreviews,
        resetState,
        createPollData,
        createSubmitData,
        setFileInputRef,
        handleMediaUpload,
        openFileSelector,
        referenceTweet,
        setReferenceTweet
    ]);
};

// Storybook에서 사용할 모의 상태 설정 함수
let mockReferenceTweet: TweetProps | ReferenceTweetType | null = null;

export const setupMockTweetStore = (tweet: TweetProps | ReferenceTweetType | null) => {
    mockReferenceTweet = tweet;
};

/**
 * 트윗 데이터 어댑터: OpenAPI 응답 형식에서 내부 타입으로 변환
 * @param tweetResponse API 응답으로 받은 트윗 데이터
 * @returns 내부 애플리케이션에서 사용하는 트윗 타입으로 변환된 데이터
 */
export const tweetDataAdapter = (tweetResponse: TweetResponse): TweetProps => {
    // 기본 트윗 정보
    const basicInfo: TweetResponseBasicInfo = tweetResponse.basicInfo || {
        id: '',
        createdAt: new Date().toISOString(),
        content: ''
    };

    // 사용자 정보 추출
    const authorInfo: BaseUserInfo = tweetResponse.author?.basicInfo || {
        id: '',
        username: '',
        displayName: ''
    };

    // 참여 통계
    const engagement = tweetResponse.engagement || {
        likeCount: '0',
        commentCount: '0',
        retweetCount: '0',
        viewCount: '0'
    };

    // 사용자 상호작용
    const userInteraction = tweetResponse.userInteraction || {
        liked: false,
        bookmarked: false
    };

    // 미디어 정보 처리
    const media = tweetResponse.media || { mediaUrls: [] };

    // 인용 또는 리트윗된 트윗 처리
    const referencedTweet = tweetResponse.referencedTweet;
    let isQuote = false;
    let isRetweet = false;
    let quotedTweet: TweetProps | undefined;
    let originalTweet: TweetProps | undefined;

    if (referencedTweet) {
        if (referencedTweet.type === 'QUOTED') {
            isQuote = true;
            if (referencedTweet.tweet) {
                quotedTweet = tweetDataAdapter(referencedTweet.tweet);
            }
        } else if (referencedTweet.type === 'RETWEETED') {
            isRetweet = true;
            if (referencedTweet.tweet) {
                originalTweet = tweetDataAdapter(referencedTweet.tweet);
            }
        }
    }

    // 변환된 트윗 객체 반환
    return {
        id: basicInfo.id || '',
        content: basicInfo.content || '',
        author: {
            id: authorInfo.id || '',
            name: authorInfo.displayName || '',
            username: authorInfo.username || '',
            profileImage: authorInfo.profileImageUrl || '',
            verified: false // API에 verified 필드가 없으므로 기본값 사용
        },
        createdAt: basicInfo.createdAt || new Date().toISOString(),
        images: media.mediaUrls || [],
        videos: [],  // API 응답에서 비디오 URL 추출 필요
        likesCount: parseInt(engagement.likeCount || '0', 10),
        repliesCount: parseInt(engagement.commentCount || '0', 10),
        retweetsCount: parseInt(engagement.retweetCount || '0', 10),
        viewsCount: parseInt(engagement.viewCount || '0', 10),
        isLiked: userInteraction.liked || false,
        isRetweetedByMe: false, // API에서 관련 필드가 없으므로 기본값 사용
        isQuote,
        quotedTweet,
        isRetweet,
        originalTweet
    };
}; 